import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpContextToken, HttpRequest } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { ICustomers, IImpersonationSession, IVendorCustomers } from '../shared/interfaces/model';
import { UserRole } from '../shared/enums/user-role';
import { TabLockService } from './tab-lock.service';

/**
 * Marks a request as part of the impersonation bootstrap, so the interceptor sends it through
 * without gating it on `ready()`. Gating one would deadlock: the run would be waiting on a request
 * that is waiting on the run.
 */
export const SKIP_IMPERSONATION_GATE = new HttpContextToken<boolean>(() => false);

@Injectable({ providedIn: 'root' })
export class ImpersonationService {
  private static readonly LOCK_PREFIX = 'hubx-impersonation:';

  // All impersonation endpoints, built from environment.adminUrl (= adminBaseUrl + 'api/').
  private static readonly BASE = environment.adminUrl + 'impersonation/';
  /** Creates a session id. Goes out plainly — it can't carry an id it's about to create. */
  private static readonly SESSION_URL = ImpersonationService.BASE + 'session';
  /** Lists the customers this user can impersonate (buyer portal). */
  private static readonly GET_CUSTOMERS_URL = ImpersonationService.BASE + 'customers';
  /** PUT {code}: set the impersonated customer (buyer portal). */
  private static readonly CUSTOMER_IMPERSONATION_URL = ImpersonationService.BASE + 'customer/';
  /** Lists the vendors this user can impersonate (vendor portal). */
  private static readonly GET_VENDORS_URL = ImpersonationService.BASE + 'vendorcustomers';
  /** PUT {code}: set the impersonated vendor (vendor portal). */
  private static readonly VENDOR_IMPERSONATION_URL = ImpersonationService.BASE + 'vendor/';

  /** The one-time run that settles this tab's id (and, on vendor portal, its company). */
  private resolvePromise?: Promise<void>;

  /** Bumped on clear(). A mint in flight when clear() runs sees a stale value and discards its
   *  result, so a logout can't be undone by a session POST that resolves afterward. */
  private generation = 0;

  /** True if this tab minted a new id (fresh/cookie login or duplicate tab) rather than reusing
   *  the stored one (plain reload). A mint also settles the session's company (see requestNew). */
  private _mintedNewSession = false;
  get mintedNewSession(): boolean {
    return this._mintedNewSession;
  }

  constructor(private http: HttpClient, private tabLock: TabLockService) {}

  /**
   * Whether a request should carry this tab's impersonation id. True for everything except the
   * bootstrap: the session call can't carry an id it's about to create, and the vendor-list read
   * that picks a vendor manager's opening company runs before that id exists.
   */
  shouldSendWithImpersonation(request: HttpRequest<unknown>): boolean {
    return request.url !== ImpersonationService.SESSION_URL
      && !request.context.get(SKIP_IMPERSONATION_GATE);
  }

  /** Customers a sales rep/super can impersonate. `showAll` adds ?showAll=true for superusers. */
  getCustomers(showAll: boolean): Observable<ICustomers> {
    const url = ImpersonationService.GET_CUSTOMERS_URL + (showAll ? '?showAll=true' : '');
    return this.http.get<ICustomers>(url);
  }

  /** Tell the backend which customer this session is now acting as. */
  selectCustomer(code: string): Observable<null> {
    return this.http.put<null>(ImpersonationService.CUSTOMER_IMPERSONATION_URL + code, '');
  }

  /** Customers a vendor manager/admin can impersonate. Read-only for callers that
   *  only need the list (e.g. the item editor's target-company selector, ADR-0010):
   *  reading it never changes the session's impersonated company — only
   *  `impersonateVendor` does that. */
  getVendorCustomers(): Observable<IVendorCustomers> {
    return this.http.get<IVendorCustomers>(ImpersonationService.GET_VENDORS_URL);
  }

  /** Tell the backend which vendor this session is now acting as. */
  impersonateVendor(code: string): Observable<null> {
    return this.http.put<null>(ImpersonationService.VENDOR_IMPERSONATION_URL + code, '');
  }

  /**
   * The company this tab is acting as: the code the session was minted on, or the one the switcher
   * last set. Written only after the backend confirms the company (see `requestNew` and
   * top-vendor.component), so its presence means the impersonation row really carries it.
   *
   * Static because it is a bare sessionStorage read — callers that only need to know what they are
   * acting as should not have to inject the service.
   */
  static get selectedCompanyCode(): string | null {
    return sessionStorage.getItem('selected_customer');
  }

  /**
   * The BP code to mint a new session on, for the given role:
   * - already acting as a company  -> keep it, whatever the role
   * - ADMIN                        -> the configured vendor (it has no company of its own)
   * - VENDORMGR                    -> the first company the backend lists
   * - anything else                -> none; the session is minted without a company
   *
   * Only the mint asks. Everyone else reads `selectedCompanyCode`, so there is one decision per
   * session rather than one per component.
   */
  async resolveDefaultVendorBP(role: string | null): Promise<string | null> {
    const acting = ImpersonationService.selectedCompanyCode;
    if (acting) {
      return acting;
    }

    switch (role) {
      case UserRole.admin:
        return environment.defaultAdminVendorBP;
      case UserRole.vendorMgr:
        return this.firstVendorBP();
      default:
        return null;
    }
  }

  /**
   * First company a vendor manager may act for, replacing the server's dropped `IsDefault` flag.
   * Positional, so this must stay the server's order — never a display sort.
   *
   * Runs inside the mint, before there is an id to send, so it bypasses the gate (see
   * SKIP_IMPERSONATION_GATE). Failing to read the list is not a reason to fail the mint: we return
   * null and the session opens with no company, which the switcher then fills in.
   */
  private async firstVendorBP(): Promise<string | null> {
    try {
      const list = await firstValueFrom(
        this.http.get<IVendorCustomers>(ImpersonationService.GET_VENDORS_URL, {
          context: new HttpContext().set(SKIP_IMPERSONATION_GATE, true)
        })
      );
      return list?.customers?.[0]?.bpCode ?? null;
    } catch (err) {
      console.error('Could not read the vendor list for the default company', err);
      return null;
    }
  }

  /** The per-tab impersonation id. Kept in sessionStorage so it survives a reload. */
  get id(): string | null {
    return sessionStorage.getItem('impersonationId');
  }

  /** Stores the id (or clears it when null). */
  private set id(value: string | null) {
    if (value) {
      sessionStorage.setItem('impersonationId', value);
    } else {
      sessionStorage.removeItem('impersonationId');
    }
  }

  /**
   * Resolves once this tab has its own impersonation id. Safe to call repeatedly and to await —
   * runs once, shared, never throws.
   *
   * `role` decides the company a newly minted session opens on (see `resolveDefaultVendorBP`).
   * Only the mint path uses it, so a plain reload never pays for resolving it.
   */
  public ready(role: string | null): Promise<void> {
    // Cache the run so we only do it once. But if the run didn't end with this tab on its own id
    // (backend was down, or we're a duplicate still holding the other tab's id), clear the cache so
    // the next request tries again and the tab can recover on its own.
    this.resolvePromise ??= this.resolveCore(role)
      .catch(err => {
        console.error('Failed to resolve impersonation id', err);
        return false;
      })
      .then(settled => {
        if (!settled) {
          this.resolvePromise = undefined; // let the next request retry
        }
      });
    return this.resolvePromise;
  }

  /**
   * Settles this tab on an id of its own. A duplicated tab copies the original's stored id, so we
   * "claim" the id (see TabLockService) to tell them apart: if the claim succeeds no other tab has
   * it (reload/fresh login — keep it); if it fails another tab owns it (duplicate — mint a new one).
   * Returns true if the id is safely ours, false if it should retry.
   */
  private async resolveCore(role: string | null): Promise<boolean> {
    const existingId = this.id;

    if (!existingId) {
      // fresh login / new tab
      return await this.requestNew(role);
    }

    // Old browsers can't claim ids, so we can't detect duplicates. Just keep the stored id.
    if (!this.tabLock.supportsLocks()) {
      return true;
    }

    // We could claim the id, so no other open tab is using it: it's ours (a reload). Keep it.
    const lockKey = ImpersonationService.LOCK_PREFIX + existingId;
    if (await this.tabLock.claim(lockKey)) {
      return true;
    }

    // We couldn't claim it, so another open tab owns it: we're a duplicate. Ask for our own id.
    // We keep the old id for now so this tab still works if the request fails; returning the
    // request's result (false on failure) makes us retry later and switch to our own id then.
    return await this.requestNew(role);
  }

  /**
   * Gets a new impersonation id from the backend, stores it, and claims it for this tab.
   * Returns true on success, false if anything went wrong.
   */
  private async requestNew(role: string | null): Promise<boolean> {
    const generation = this.generation;
    try {
      // The company the session opens on: the user's pick, else the role's default, else '' as
      // before any default existed.
      const companyCode = (await this.resolveDefaultVendorBP(role)) || '';

      const result = await firstValueFrom(
        this.http.post<IImpersonationSession>(
          ImpersonationService.SESSION_URL,
          { companyCode }
        )
      );

      if (!result?.impersonationId) {
        console.error('Backend did not return an impersonation id.');
        return false;
      }

      // A clear() (logout) happened while the POST was in flight — discard this id so we don't
      // re-write keys the logout just removed.
      if (generation !== this.generation) {
        return false;
      }

      this.id = result.impersonationId;
      this._mintedNewSession = true;

      // Record the company the session was minted on, so a reload or duplicate tab re-mints on the
      // company in use rather than back on the role default. Taken from the response, not from what
      // we sent: the backend creates the session without an impersonation row when it refuses the
      // code, and storing our request would leave us claiming a company we are not acting as.
      if (result.companyCode) {
        sessionStorage.setItem('selected_customer', result.companyCode);
      } else if (companyCode) {
        // Refused, not failed — the code is simply not in this user's list (the POST itself
        // succeeded). Drop it so the next mint falls back to the role default instead of asking
        // for the same rejected company forever. This session has no company until one is picked.
        console.error(`Impersonation session was not linked to requested company ${companyCode}`);
        sessionStorage.removeItem('selected_customer');
      }

      const lockKey = ImpersonationService.LOCK_PREFIX + result.impersonationId;
      await this.tabLock.claim(lockKey);
      // A clear() during the claim means we just locked an id that's no longer ours — release it.
      if (generation !== this.generation) {
        this.tabLock.release(lockKey);
        return false;
      }
      return true;
    } catch (err) {
      // Backend is down: don't block the user. Requests go out with no impersonation header, which
      // the server treats as "not impersonating". Returning false clears the cached run (see ready),
      // so the next request retries and gets an id once the backend is back.
      console.error('Failed to create impersonation session', err);
      return false;
    }
  }

  /** Logout: release the lock, drop the stored id, and reset the cached run. */
  clear(): void {
    // Invalidate any in-flight mint so it can't re-write the keys we're about to remove.
    this.generation++;
    const id = this.id;
    if (id) {
      this.tabLock.release(ImpersonationService.LOCK_PREFIX + id);
    }
    this.resolvePromise = undefined;
    this._mintedNewSession = false;
    this.id = null;
  }
}
